"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Cpu, Wallet, ShieldCheck, Coins, Zap, Layers, ArrowRight } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const specs = [{
  title: "Infinitely Scalable AI Compute",
  description: "Unlock massive processing power through decentralized networks, ensuring your enterprise applications never hit a bottleneck.",
  icon: Cpu
}, {
  title: "Secure Network Processing",
  description: "Enterprise-grade execution with cryptographic verification, guaranteeing data integrity across all distributed nodes.",
  icon: ShieldCheck
}, {
  title: "Stablecoin Rewards (USDC)",
  description: "Earn predictable, stable yields for contributing compute resources, seamlessly integrated into the OceanWallet ecosystem.",
  icon: Coins
}, {
  title: "Personal Agent Allocation",
  description: "Instantly apply earned compute credits toward your own autonomous AI agents for automated business tasks.",
  icon: Zap
}, {
  title: "Apple Business Integration",
  description: "Native, high-fidelity experiences designed specifically for macOS and iOS environments, ensuring seamless enterprise adoption.",
  icon: Layers
}, {
  title: "Crypto Gateway Flexibility",
  description: "Frictionless swapping between compute credits and USDC, providing ultimate liquidity and operational agility.",
  icon: Wallet
}];
interface SpecChipProps {
  title: string;
  description: string;
  icon: React.ElementType;
  index: number;
}
function SpecChip({
  title,
  description,
  icon: Icon,
  index
}: SpecChipProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.6,
    once: false
  });
  return <div ref={ref} data-index={index} className={cn("group relative flex flex-col sm:flex-row items-start gap-5 p-6 rounded-2xl border transition-all duration-700 ease-out", isVisible ? "border-primary/50 bg-primary/5 shadow-[0_0_40px_-10px_var(--color-primary)] translate-y-0 opacity-100" : "border-border/40 bg-card/30 translate-y-8 opacity-40 hover:border-border/80")}>
      <div className={cn("flex shrink-0 items-center justify-center size-12 rounded-xl transition-colors duration-500", isVisible ? "bg-primary text-primary-foreground shadow-[0_0_20px_var(--color-primary)]" : "bg-muted text-muted-foreground")}>
        <Icon className="size-6" />
      </div>
      <div className="space-y-2">
        <h4 className={cn("text-xl font-semibold font-default transition-colors duration-500", isVisible ? "text-foreground" : "text-muted-foreground")}>
          {title}
        </h4>
        <p className="text-muted-foreground font-sans leading-relaxed">
          {description}
        </p>
      </div>
    </div>;
}
export default function ActiveInnovationOceanProtocol() {
  return <section id="active-innovation-ocean-protocol" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] bg-primary/10 rounded-full blur-[120px] opacity-50 mix-blend-screen" />
        <div className="absolute bottom-0 right-0 w-[800px] h-[800px] bg-secondary/5 rounded-full blur-[100px] opacity-30 mix-blend-screen" />
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1633370/img/noise_texture_dark.png)] opacity-[0.03] mix-blend-overlay" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-start">
          
          {}
          <div className="lg:col-span-5 relative lg:sticky lg:top-32 h-auto lg:h-[calc(100vh-16rem)] flex flex-col justify-center">
            <div className="relative w-full max-w-md mx-auto lg:max-w-none">
              {}
              <div className="relative z-10 rounded-2xl overflow-hidden border border-border/50 shadow-2xl shadow-black/50 bg-card/50 backdrop-blur-sm transform transition-transform duration-1000 hover:scale-[1.02]">
                <div className="absolute top-0 inset-x-0 h-6 bg-muted/80 backdrop-blur-md flex items-center px-3 gap-1.5 border-b border-border/50">
                  <div className="size-2.5 rounded-full bg-destructive/80" />
                  <div className="size-2.5 rounded-full bg-secondary/80" />
                  <div className="size-2.5 rounded-full bg-green-500/80" />
                </div>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1633370/img/oceanclaw_dashboard_mac_mockup.png" alt="OceanClaw AI Compute Dashboard on macOS" className="w-full h-auto object-cover pt-6" />
              </div>

              {}
              <div className="absolute -bottom-12 -right-6 w-2/5 z-20 rounded-[2rem] overflow-hidden border-[6px] border-muted shadow-2xl shadow-black/80 transform transition-transform duration-1000 hover:-translate-y-4">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1633370/img/oceanwallet_mobile_app_ios.png" alt="OceanWallet Crypto Gateway App on iOS" className="w-full h-auto object-cover" />
              </div>

              {}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 bg-primary/20 blur-[80px] -z-10 rounded-full" />
            </div>
          </div>

          {}
          <div className="lg:col-span-7 flex flex-col gap-12">
            
            {}
            <div className="space-y-6">
              <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 px-4 py-1.5 text-sm uppercase tracking-widest font-sans">
                Ocean Protocol Ecosystem
              </Badge>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default leading-tight tracking-tight">
                Active Innovation: <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                  Ocean Protocol
                </span>
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground font-sans max-w-2xl leading-relaxed">TidalSwing AI is not just a consultancy; we are sophisticated builders of scalable digital ecosystems. OceanClaw and OceanWallet blend best-in-class AI computing with secure crypto gateways, powering the future of applied digital infrastructure.</p>
            </div>

            {}
            <div className="flex flex-col gap-6">
              {specs.map((spec, i) => <SpecChip key={i} index={i} title={spec.title} description={spec.description} icon={spec.icon} />)}
            </div>

            {}
            <div className="pt-8 flex flex-col sm:flex-row items-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto h-14 px-8 text-base font-semibold rounded-full shadow-[0_0_20px_rgba(var(--color-primary),0.4)] hover:shadow-[0_0_30px_rgba(var(--color-primary),0.6)] transition-shadow">
                <Link to="#">
                  Explore Model Context Protocol
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto h-14 px-8 text-base font-semibold rounded-full border-border/50 hover:bg-muted/50 backdrop-blur-sm">
                <Link to="#">
                  View Technical Docs
                </Link>
              </Button>
            </div>

          </div>
        </div>
      </div>
    </section>;
}